import { Color3, HemisphericLight, MeshBuilder, SceneLoader, StandardMaterial, TransformNode, Vector3 } from "@babylonjs/core";
import "@babylonjs/loaders/glTF/index.js";

export const PROTAGONIST_CONFIG = Object.freeze({
  asset: "/game/assets/characters/protagonist/protagonist-animated.glb",
  baseScale: .17,
  rotationOffset: 0,
  modelForwardOffset: 0,
  verticalOffset: 0,
  calibratedMovementSpeed: .09,
  crossFadeSeconds: .16,
  turnSpeed: 7,
  movementDeadzone: 1e-5,
  turnEpsilon: .02
});

export const normalizeAngle = angle => Math.atan2(Math.sin(angle), Math.cos(angle));
export const headingFromMovement = ({ x, y }, offset = 0) => normalizeAngle(Math.atan2(x, -y) + offset);
export function stepAngle(current, target, maximumStep) {
  const delta = normalizeAngle(target - current);
  return normalizeAngle(current + Math.sign(delta) * Math.min(Math.abs(delta), maximumStep));
}

function splitAssetUrl(asset) {
  const slash = asset.lastIndexOf("/");
  return { rootUrl: asset.slice(0, slash + 1), fileName: asset.slice(slash + 1) };
}

function createFallback(scene, root) {
  const material = new StandardMaterial("character-fallback-material", scene);
  material.diffuseColor = Color3.FromHexString("#ef742d");
  material.emissiveColor = Color3.FromHexString("#6d260d");
  const mesh = MeshBuilder.CreateCapsule("character-fallback", { height: 1.8, radius: .32, tessellation: 12 }, scene);
  mesh.material = material;
  mesh.position.y = .9;
  mesh.parent = root;
  return mesh;
}

export async function createCharacterModel(scene, options = {}) {
  const config = { ...PROTAGONIST_CONFIG, ...options };
  const root = new TransformNode("protagonist-position-root", scene);
  const light = new HemisphericLight("protagonist-fill-light", new Vector3(0, 1, -1), scene);
  light.intensity = 1.35;
  light.diffuse = new Color3(1, .93, .86);
  light.groundColor = new Color3(.16, .2, .28);
  const state = {
    asset: config.asset, loaded: false, fallback: false, error: null,
    currentAnimation: null, movementState: "idle", configuredMovementSpeed: 0, movementSpeed: 0, movementVector: { x: 0, y: 0 }, animationSpeedRatio: 1,
    modelScale: config.baseScale, yaw: config.rotationOffset + config.modelForwardOffset, targetHeading: config.rotationOffset + config.modelForwardOffset,
    rotationOffset: config.rotationOffset, modelForwardOffset: config.modelForwardOffset, turnState: "idle", switches: 0,
    meshCount: 0, textureCount: 0, animationGroupNames: [], animationGroups: []
  };
  let groups = {}, blend = null, controller = null, yawInitialized = false, characterMeshes = [], sceneOffset={x:0,y:0},cinematicVisible=true;

  try {
    const { rootUrl, fileName } = splitAssetUrl(config.asset);
    const result = await SceneLoader.ImportMeshAsync("", rootUrl, fileName, scene);
    const importedRoot = result.meshes[0];
    if (!importedRoot) throw new Error("GLB did not contain a mesh root.");
    importedRoot.parent = root;
    characterMeshes = result.meshes.filter(mesh => typeof mesh.getBoundingInfo === "function");
    groups = Object.fromEntries(result.animationGroups.map(group => [group.name, group]));
    state.meshCount = result.meshes.length;
    state.textureCount = new Set(result.meshes.flatMap(mesh => mesh.material?.getActiveTextures?.() || [])).size;
    state.animationGroupNames = result.animationGroups.map(group => group.name);
    if (!groups.Idle || !groups.Walk) throw new Error(`Expected animation groups Idle and Walk; found: ${result.animationGroups.map(group => group.name).join(", ") || "none"}.`);
    result.animationGroups.filter(group => ![groups.Idle, groups.Walk].includes(group)).forEach(group => group.stop());
    groups.Idle.enableBlending = false; groups.Walk.enableBlending = false;
    groups.Idle.start(true, 1); groups.Walk.start(true, 1);
    groups.Idle.setWeightForAllAnimatables(1); groups.Walk.setWeightForAllAnimatables(0);
    state.loaded = true; state.currentAnimation = "Idle";
  } catch (error) {
    state.error = error instanceof Error ? error.message : String(error);
    state.fallback = true;
    characterMeshes = [createFallback(scene, root)];
  }

  function setAnimation(name, movementSpeed = 0) {
    const walkRatio = Math.max(.05, movementSpeed / config.calibratedMovementSpeed);
    if (!state.loaded || state.currentAnimation === name) {
      if (name === "Walk" && groups.Walk) {
        state.animationSpeedRatio = walkRatio; groups.Walk.speedRatio = walkRatio;
      }
      return;
    }
    const previous = state.currentAnimation;
    state.currentAnimation = name; state.switches += 1;
    blend = { from: previous, to: name, elapsed: 0 };
    if (name === "Walk") {
      state.animationSpeedRatio = walkRatio; groups.Walk.speedRatio = walkRatio;
    } else state.animationSpeedRatio = 1;
  }

  function updateAnimationDebug() {
    state.animationGroups = Object.values(groups).map(group => {
      const weights = group.animatables.map(animatable => animatable.weight);
      const frame = group.getCurrentFrame(), range = Math.max(1e-7, group.to - group.from);
      return { name: group.name, isPlaying: group.isPlaying, frame, progress: ((frame - group.from) % range + range) % range / range,
        weight: weights.length ? weights.reduce((sum, weight) => sum + weight, 0) / weights.length : 0,
        speedRatio: group.speedRatio, loopAnimation: group.loopAnimation };
    });
  }

  return {
    root, state, config, groups, light, get meshes(){return characterMeshes},
    sync(nextController) {
      controller = nextController;
      const aspect = scene.getEngine().getRenderWidth() / Math.max(1, scene.getEngine().getRenderHeight());
      root.position.x = ((controller.position.x+sceneOffset.x) * 2 - 1) * aspect;
      root.position.y = 1 - (controller.position.y+sceneOffset.y) * 2 + config.verticalOffset * controller.scale;
      root.position.z = 0;
      const scale = config.baseScale * controller.scale;
      root.scaling.setAll(scale);
      const vector = controller.movementVector || { x: controller.facing === "left" ? -1 : 1, y: 0 };
      if (Math.hypot(vector.x, vector.y) >= config.movementDeadzone) state.targetHeading = headingFromMovement(vector, config.rotationOffset + config.modelForwardOffset);
      if (!yawInitialized) { root.rotation.y = state.targetHeading; yawInitialized = true; }
      state.modelScale = scale; state.yaw = root.rotation.y;
      state.movementState = controller.isMoving ? "moving" : "idle";
      state.configuredMovementSpeed = controller.configuredMovementSpeed ?? controller.speed ?? 0;
      state.movementSpeed = controller.movementSpeed;
      state.movementVector = { ...vector };
      setAnimation(controller.isMoving ? "Walk" : "Idle", controller.movementSpeed);
    },
    setSceneOffset(offset={}){sceneOffset={x:Number(offset.x)||0,y:Number(offset.y)||0};if(controller)this.sync(controller)},
    setCinematicVisible(visible){cinematicVisible=!!visible;root.setEnabled(cinematicVisible)},
    update(deltaSeconds) {
      const previousYaw = root.rotation.y;
      root.rotation.y = stepAngle(previousYaw, state.targetHeading, config.turnSpeed * deltaSeconds);
      const remaining = Math.abs(normalizeAngle(state.targetHeading - root.rotation.y));
      state.yaw = root.rotation.y; state.turnState = remaining > config.turnEpsilon ? "turning" : "aligned";
      if (controller?.turning && remaining <= config.turnEpsilon) controller.finishTurn();
      if (blend && state.loaded) {
        blend.elapsed += deltaSeconds;
        const weight = Math.min(1, blend.elapsed / config.crossFadeSeconds);
        groups[blend.from]?.setWeightForAllAnimatables(1 - weight);
        groups[blend.to]?.setWeightForAllAnimatables(weight);
        if (weight >= 1) blend = null;
      }
      if (state.loaded) updateAnimationDebug();
    },
    dispose() { root.dispose(false, true); light.dispose(); }
  };
}
